import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import axios from 'axios';
import { API_BASE_URL } from '../../services/apiConfig';
import { useSiteSettings } from '../../context/SiteSettingsContext';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const uid = params.get('uid') || '';
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const { settings } = useSiteSettings();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const EyeIcon = ({ visible }) => visible ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.77 21.77 0 0 1 5.06-6.06"/><path d="M22.54 12.88A21.77 21.77 0 0 0 23 12s-4-8-11-8a10.94 10.94 0 0 0-3.66.64"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
  );

  const missingLink = !uid || !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/accounts/password-reset-confirm/`, {
        uid, token, new_password: password,
      });
      toast.success('Password reset. You can now sign in.');
      navigate('/login');
    } catch (err) {
      const data = err.response?.data;
      const msg =
        data?.detail ||
        (data?.new_password && data.new_password.join(' ')) ||
        'Reset link is invalid or has expired. Request a new one.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledWrapper>
      <div className="container">
        <div className="card">
          <img src={settings?.header_logo || '/APPSN.jpg'} alt="APPSN" className="logo" />
          <span className="title">Set new password</span>
          <p className="subtitle">APPSN - FCT Branch</p>

          {missingLink ? (
            <div className="error-box">
              <p>This reset link is incomplete or malformed. Please request a new one.</p>
              <Link to="/forgot-password" className="back-link">Request a new link</Link>
            </div>
          ) : (
            <>
              <p className="hint">
                Choose a new password for your APPSN account. Minimum 8 characters.
              </p>
              <form onSubmit={handleSubmit}>
                <div className="inputBox hasToggle">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <span>New Password</span>
                  <button
                    type="button"
                    className="eyeBtn"
                    onClick={() => setShowPassword(s => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  ><EyeIcon visible={showPassword} /></button>
                </div>
                <div className="inputBox hasToggle">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                  <span>Confirm Password</span>
                  <button
                    type="button"
                    className="eyeBtn"
                    onClick={() => setShowConfirm(s => !s)}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  ><EyeIcon visible={showConfirm} /></button>
                </div>
                <button
                  className="enter"
                  type="submit"
                  disabled={loading || !password || !confirm}
                >
                  {loading ? 'Saving...' : 'Set password'}
                </button>
              </form>
              <p className="register-link">
                <Link to="/login">Back to sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </StyledWrapper>
  );
}

const StyledWrapper = styled.div`
  .container {
    min-height: 70vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    background: #F8FAFB;
  }
  .card {
    display: flex; flex-direction: column; align-items: center; gap: 22px;
    width: 380px; max-width: 95vw; background: #e3e3e3;
    box-shadow: 16px 16px 32px #c8c8c8, -16px -16px 32px #fefefe;
    border-radius: 12px; padding: 2.5rem 1.5rem;
  }
  .logo { height: 80px; object-fit: contain; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
  .title {
    color: #0B5D1E; text-transform: uppercase; letter-spacing: 2px;
    font-weight: bold; font-size: x-large; font-family: 'Poppins', sans-serif;
  }
  .subtitle { color: #6b7280; font-size: 0.875rem; margin-top: -15px; }
  .hint { color: #374151; font-size: 0.875rem; text-align: center; padding: 0 6px; margin: 0; }
  form { display: flex; flex-direction: column; align-items: center; gap: 22px; width: 100%; }
  .inputBox { position: relative; width: 280px; max-width: 100%; }
  .inputBox input {
    width: 100%; padding: 12px; outline: none; border: none; color: #000;
    font-size: 1em; background: transparent;
    border-left: 2px solid #0B5D1E; border-bottom: 2px solid #0B5D1E;
    transition: 0.1s; border-bottom-left-radius: 8px;
  }
  .inputBox span {
    margin-top: 5px; position: absolute; left: 0; transform: translateY(-4px);
    margin-left: 10px; padding: 10px; pointer-events: none; font-size: 12px;
    color: #374151; text-transform: uppercase; transition: 0.5s; letter-spacing: 3px;
    border-radius: 8px;
  }
  .inputBox input:valid ~ span, .inputBox input:focus ~ span {
    transform: translateX(113px) translateY(-15px); font-size: 0.8em;
    padding: 5px 10px; background: #0B5D1E; letter-spacing: 0.2em; color: #fff;
  }
  .inputBox input:valid, .inputBox input:focus {
    border: 2px solid #0B5D1E; border-radius: 8px;
  }
  .inputBox.hasToggle input { padding-right: 40px; }
  .eyeBtn {
    position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
    background: transparent; border: none; cursor: pointer; padding: 4px;
    display: flex; align-items: center; justify-content: center;
    color: #0B5D1E; border-radius: 4px;
  }
  .eyeBtn:hover { background: rgba(11, 93, 30, 0.08); }
  .eyeBtn:focus { outline: 2px solid #0B5D1E; outline-offset: 1px; }
  .enter {
    height: 48px; width: 180px; border-radius: 8px; border: 2px solid #0B5D1E;
    cursor: pointer; background-color: transparent; transition: 0.3s;
    text-transform: uppercase; font-size: 0.8rem; font-weight: 600;
    letter-spacing: 2px; color: #0B5D1E;
  }
  .enter:hover { background-color: #0B5D1E; color: white; }
  .enter:disabled { opacity: 0.5; cursor: not-allowed; }
  .register-link { font-size: 0.875rem; color: #6b7280; margin: 0; }
  .register-link a, .back-link {
    color: #0B5D1E; font-weight: 600; text-decoration: none;
  }
  .register-link a:hover, .back-link:hover { text-decoration: underline; }
  .error-box {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    text-align: center; padding: 8px 4px;
  }
  .error-box p { margin: 0; font-size: 0.9rem; color: #b91c1c; }
`;
